/**
 * RoleEditor — create or edit a role.
 *
 * Modes:
 *   - Create (no `role` prop): key (slug) + name + description + permission
 *     checklist. POSTs to /v1/admin/roles.
 *   - Edit (`role` prop set): name + description + permission checklist.
 *     key is shown read-only (never editable). System roles cannot have
 *     name/description changed (inputs disabled) but their permission set
 *     IS editable, mirroring the backend guard in updateRole(). PUTs to
 *     /v1/admin/roles/:id.
 *
 * The permission catalog is fetched from /v1/admin/permissions and grouped
 * by namespace. Destructive permissions are flagged in red.
 *
 * Validation is server-authoritative — BadRequestError / ConflictError
 * messages from the API surface inline.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §8.3.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { X, AlertTriangle } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { toast } from '@/hooks/useToast';

interface Permission {
  id: string;
  key: string;
  namespace: string;
  action: string;
  label: string;
  description: string;
  is_destructive: boolean;
}

export interface RoleDetail {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  member_count: number;
  permission_keys: string[];
}

interface Props {
  /** Omit to open the editor in create mode. */
  role?: RoleDetail | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

// Auto-derive a slug key from the name on the create form. Mirrors the
// backend ROLE_KEY_PATTERN (lowercase letters/digits/underscore, leading
// letter).
function deriveKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, '_$1');
}

export function RoleEditor({ role, onClose, onSuccess }: Props) {
  const apiUrl = useApiUrl();
  const isEdit = !!role;
  const isSystem = !!role?.is_system;

  const [name, setName] = useState(role?.name ?? '');
  const [keyValue, setKeyValue] = useState(role?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(isEdit);
  const [description, setDescription] = useState(role?.description ?? '');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    new Set(role?.permission_keys ?? []),
  );
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permsLoading, setPermsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/permissions`);
        const data: Permission[] = await res.json();
        if (!cancelled) setPermissions(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load permissions');
      } finally {
        if (!cancelled) setPermsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  // Auto-derive the key from the name on create, until the user edits it.
  useEffect(() => {
    if (!isEdit && !keyTouched) {
      setKeyValue(deriveKey(name));
    }
  }, [name, isEdit, keyTouched]);

  const grouped = useMemo(() => {
    const byNs = new Map<string, Permission[]>();
    for (const p of permissions) {
      const arr = byNs.get(p.namespace) ?? [];
      arr.push(p);
      byNs.set(p.namespace, arr);
    }
    return [...byNs.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [permissions]);

  const togglePermission = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleNamespace = (perms: Permission[]) => {
    const allSelected = perms.every(p => selectedKeys.has(p.key));
    setSelectedKeys(prev => {
      const next = new Set(prev);
      for (const p of perms) {
        if (allSelected) next.delete(p.key);
        else next.add(p.key);
      }
      return next;
    });
  };

  const canSubmit =
    name.trim().length > 0 &&
    (isEdit || keyValue.trim().length > 0) &&
    !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const permission_keys = [...selectedKeys];
    try {
      if (isEdit && role) {
        // System roles: only the permission set may change.
        const body = isSystem
          ? { permission_keys }
          : { name: name.trim(), description: description.trim(), permission_keys };
        await adminFetch(`${apiUrl}/v1/admin/roles/${role.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        toast({ title: 'Role updated', description: `${role.name} saved.` });
      } else {
        await adminFetch(`${apiUrl}/v1/admin/roles`, {
          method: 'POST',
          body: JSON.stringify({
            key: keyValue.trim().toLowerCase(),
            name: name.trim(),
            description: description.trim(),
            permission_keys,
          }),
        });
        toast({ title: 'Role created', description: `${name.trim()} is ready.` });
      }
      await onSuccess();
    } catch (e: any) {
      setError(e?.message || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-card">
        <div className="flex items-start justify-between border-b p-6">
          <div>
            <h3 className="text-lg font-semibold">
              {isEdit ? `Edit role: ${role?.name}` : 'Create role'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? 'Adjust the name, description, and permission set.'
                : 'Define a name, key, and the permissions this role grants.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1 hover:bg-muted disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
            {isSystem && (
              <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This is a system role. Its name and key are locked, but you
                  can still adjust which permissions it grants.
                </span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="role-name" className="mb-1 block text-sm font-medium">
                  Name
                </label>
                <input
                  id="role-name"
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={submitting || isSystem}
                  className="block w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
                  placeholder="Support"
                />
              </div>
              <div>
                <label htmlFor="role-key" className="mb-1 block text-sm font-medium">
                  Key {isEdit && <span className="text-muted-foreground">(read-only)</span>}
                </label>
                <input
                  id="role-key"
                  type="text"
                  required
                  value={keyValue}
                  onChange={e => {
                    setKeyTouched(true);
                    setKeyValue(e.target.value);
                  }}
                  disabled={submitting || isEdit}
                  className="block w-full rounded-md border bg-background px-3 py-2 font-mono text-sm disabled:opacity-50"
                  placeholder="support"
                />
              </div>
            </div>

            <div>
              <label htmlFor="role-desc" className="mb-1 block text-sm font-medium">
                Description
              </label>
              <textarea
                id="role-desc"
                rows={2}
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={submitting || isSystem}
                className="block w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
                placeholder="What this role is for…"
              />
            </div>

            <fieldset className="rounded-md border">
              <legend className="mx-3 px-1 text-sm font-medium">
                Permissions ({selectedKeys.size} selected)
              </legend>
              {permsLoading ? (
                <p className="p-4 text-sm text-muted-foreground">
                  Loading permissions…
                </p>
              ) : grouped.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  No permissions defined.
                </p>
              ) : (
                <div className="divide-y">
                  {grouped.map(([ns, perms]) => {
                    const allSelected = perms.every(p => selectedKeys.has(p.key));
                    const someSelected = perms.some(p => selectedKeys.has(p.key));
                    return (
                      <div key={ns} className="p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-semibold capitalize">
                            {ns}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleNamespace(perms)}
                            disabled={submitting}
                            className="text-xs text-primary hover:underline disabled:opacity-50"
                          >
                            {allSelected
                              ? 'Deselect all'
                              : someSelected
                                ? 'Select all'
                                : 'Select all'}
                          </button>
                        </div>
                        <div className="grid gap-1 sm:grid-cols-2">
                          {perms.map(p => (
                            <label
                              key={p.id}
                              className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-muted"
                            >
                              <input
                                type="checkbox"
                                checked={selectedKeys.has(p.key)}
                                onChange={() => togglePermission(p.key)}
                                disabled={submitting}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <div
                                  className={
                                    p.is_destructive
                                      ? 'text-sm font-medium text-destructive'
                                      : 'text-sm font-medium'
                                  }
                                >
                                  {p.label}
                                  {p.is_destructive && (
                                    <span className="ml-1 text-xs">(destructive)</span>
                                  )}
                                </div>
                                <code className="text-xs text-muted-foreground">
                                  {p.key}
                                </code>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </fieldset>

            {error && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t p-6">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border bg-card px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Create role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
